/* Knives & Stones signup — domain typo suggestion.
 *
 * WHY. Production bounce data: gmail.con 17 · gamil.com 8 · gmai.com 8 ·
 * gnail.com 3 · hotmail.con 3 · hormail.com 2. Forty-one people asked for the
 * newsletter and silently received nothing. `gmail.con` is syntactically valid,
 * so the form's EMAIL_RE passes it happily and always will — the only place this
 * is catchable is here, against a list of domains we know.
 *
 * THIS SUGGESTS, IT NEVER BLOCKS. suggest() returns a corrected address or null;
 * the caller offers it and the visitor may ignore it. A form that refuses a valid
 * address is a worse defect than a bounce, so every ambiguity resolves to null.
 *
 * WHY EDIT DISTANCE ALONE IS NOT ENOUGH (the load-bearing bit — do not "simplify"
 * this to a bare distance threshold). Measured against every distinct domain in
 * production `persons` + `signups` (2,292 of them, tests/storefront/fixtures/
 * real-email-domains.txt), a plain distance rule wrongly corrects:
 *
 *     ymail.com  -> gmail.com    43 real people. Yahoo's own alias domain.
 *     mail.com   -> gmail.com    19 real people. A genuine free provider.
 *     email.com  -> gmail.com     1 real person.
 *
 * All three are exactly one edit from gmail.com with an identical TLD, which is
 * also exactly what gnail.com is. No distance threshold can separate them, so
 * KNOWN_GOOD does it by name and is checked first.
 *
 * THE SHAPE OF THE RULE. Compare the second-level label and the TLD separately,
 * and allow one edit in ONE of them, never both:
 *
 *   sld matches exactly, tld is one edit    gmail.con, gmail.co, gmail.coma
 *   tld matches exactly, sld is one edit    gnail.com, gmai.com, hormail.com
 *
 * Splitting at the FIRST dot is what keeps the Australian long tail safe:
 * hotmail.co.uk / yahoo.com.au / live.com.au / outlook.co.nz all have an exact
 * sld and a TLD three edits away, so they fall out with no suggestion. And
 * requiring the provider's sld to be >= 5 characters before allowing an sld edit
 * keeps `me.com` from swallowing be.com / we.com and `live.com` from swallowing
 * life.com — those two are only reachable through the exact-sld branch.
 *
 * Distance is Damerau-Levenshtein, so a transposition costs 1 rather than 2.
 * That is not academic: transposed spellings are a real share of the production
 * data (gamil.com 12, gmial.com 3, hotamil.com and htomail.com 1 each), and
 * under plain Levenshtein every one of them costs 2 — so catching them would
 * mean loosening the threshold to 2 everywhere, which is exactly what starts
 * "correcting" ymail.com and mail.com.
 */
(function (global) {
  'use strict';

  // Suggestion targets. Ordered by real volume in production `persons`:
  // gmail 18329 · hotmail 3380 · yahoo 972 · outlook 696 · icloud 593 ·
  // bigpond 383 · live 237 · me 226. Anything rarer is not worth the
  // false-positive risk of a wider net.
  var PROVIDERS = [
    'gmail.com',
    'hotmail.com',
    'yahoo.com',
    'outlook.com',
    'icloud.com',
    'bigpond.com',
    'live.com',
    'me.com'
  ];

  // Real domains that sit inside the near-miss radius above. Checked BEFORE the
  // distance rule, because the rule provably cannot tell them from a typo.
  // Membership rule for future edits: add a domain here only if it is real AND
  // suggest() would otherwise flag it — not as a general directory of providers.
  var KNOWN_GOOD = [
    'ymail.com',   // Yahoo alias domain; 43 real people in production
    'mail.com',    // 1&1 free provider; 19 real people
    'email.com',   // mail.com sibling domain
    'wahoo.com',   // real company, one edit from yahoo.com
    'cloud.com'    // real company, one edit from icloud.com
  ];

  // suggest() runs on every keystroke, so a quadratic distance over an unbounded
  // paste is a jank risk on a phone. Nothing legitimate is anywhere near this.
  var MAX_DOMAIN_LEN = 64;

  function indexOfIn(list, value) {
    for (var i = 0; i < list.length; i++) {
      if (list[i] === value) return i;
    }
    return -1;
  }

  /* Damerau-Levenshtein (optimal string alignment). Adjacent transposition costs
   * 1, which is what makes gamil/gmial reachable at a threshold of one edit. */
  function distance(a, b) {
    var la = a.length;
    var lb = b.length;
    if (Math.abs(la - lb) > 2) return 99; // cheap bail; we only ever ask about 1
    var prev2 = [];
    var prev = [];
    var cur = [];
    var i, j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      cur[0] = i;
      for (j = 1; j <= lb; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 &&
            a.charAt(i - 1) === b.charAt(j - 2) &&
            a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur[j] = v;
      }
      prev2 = prev.slice();
      prev = cur.slice();
    }
    return prev[lb];
  }

  // Split at the FIRST dot: 'hotmail.co.uk' -> ['hotmail', 'co.uk'].
  function split(domain) {
    var i = domain.indexOf('.');
    if (i <= 0 || i === domain.length - 1) return null;
    return [domain.slice(0, i), domain.slice(i + 1)];
  }

  /* Returns the corrected domain, or null when the domain is fine or unknown. */
  function suggestDomain(domain) {
    if (typeof domain !== 'string') return null;
    var d = domain.trim().toLowerCase();
    if (!d || d.length > MAX_DOMAIN_LEN) return null;
    if (indexOfIn(PROVIDERS, d) !== -1) return null;
    if (indexOfIn(KNOWN_GOOD, d) !== -1) return null;

    var parts = split(d);
    if (!parts) return null;
    var sld = parts[0];
    var tld = parts[1];

    for (var i = 0; i < PROVIDERS.length; i++) {
      var p = split(PROVIDERS[i]);
      // A typo in the TLD, on a provider name spelled correctly.
      if (sld === p[0] && distance(tld, p[1]) === 1) return PROVIDERS[i];
      // A typo in the provider name, on a TLD spelled correctly. Gated on the
      // provider's own length: one edit of 'me' or 'live' is too easily a
      // different real domain.
      if (tld === p[1] && p[0].length >= 5 && distance(sld, p[0]) === 1) {
        return PROVIDERS[i];
      }
    }
    return null;
  }

  /* The public entry point. Takes what is in the input box, returns the whole
   * corrected address (local part untouched) or null. Never throws. */
  function suggest(email) {
    if (typeof email !== 'string') return null;
    var value = email.trim();
    var at = value.lastIndexOf('@');
    if (at <= 0 || at === value.length - 1) return null;
    var local = value.slice(0, at);
    if (local.indexOf('@') !== -1) return null;
    var fixed = suggestDomain(value.slice(at + 1));
    return fixed ? local + '@' + fixed : null;
  }

  var api = {
    suggest: suggest,
    suggestDomain: suggestDomain,
    PROVIDERS: PROVIDERS,
    KNOWN_GOOD: KNOWN_GOOD
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  } else {
    global.KnsEmailTypo = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
